import { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiHelpers } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore, isGhostSession, getGhostInfo } from '@/stores/auth';
import { useTheme } from '@/hooks/useTheme';
import { cn } from '@/lib/utils';
import { can } from '@/types';
import type { Capability } from '@/types';
import { UserCell } from '@/components/ui/user-cell';
import { RoleBadge } from '@/components/ui/role-badge';
import { UserRulesNotice } from '@/components/legal/UserRulesNotice';
import {
  LayoutDashboard,
  FolderOpen,
  Users,
  UsersRound,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Moon,
  Sun,
  Brain,
  CreditCard,
  Receipt,
  Activity,
  Wallet,
  DollarSign,
  Shield,
  Link2 as LinkIcon,
  Sparkles,
  LineChart,
  BookUser,
} from 'lucide-react';

const mainNavigation: {
  name: string;
  href: string;
  icon: any;
  requires?: Capability;
  superOnly?: boolean;
}[] = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Website', href: '/website', icon: Sparkles },
  { name: 'Projects', href: '/projects', icon: FolderOpen },
  { name: 'AI Training', href: '/ai-training', icon: Brain, requires: 'manage_ai_training' },
  { name: 'Users', href: '/users', icon: Users, superOnly: true },
];

const settingsNavigation: { name: string; href: string; icon: any; requires?: Capability }[] = [
  { name: 'General', href: '/settings', icon: Settings },
  { name: 'Team', href: '/settings/team', icon: UsersRound, requires: 'invite_members' },
  { name: 'Billing', href: '/settings/billing', icon: CreditCard, requires: 'manage_billing' },
  {
    name: 'Invoices',
    href: '/settings/billing/invoices',
    icon: Receipt,
    requires: 'manage_billing',
  },
];

const adminNavigation = [
  { name: 'Platform insights', href: '/admin/insights', icon: LineChart },
  { name: 'SRE Dashboard', href: '/admin/sre', icon: Activity },
  { name: 'Admin Billing', href: '/admin/billing', icon: Wallet },
  { name: 'Payment Links', href: '/admin/payment-links', icon: LinkIcon },
  { name: 'AI Cost Monitor', href: '/admin/costs', icon: DollarSign },
  { name: 'Integrations', href: '/admin/integrations', icon: Shield },
  { name: 'Subscriber register', href: '/admin/subscriber-register', icon: BookUser },
];

export default function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { isDark, setTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();

  // Suspended-project banner. This layout is the PARENT route, so useParams()
  // can't see :projectId — derive it from the pathname. Shares qk.project(id)
  // with ProjectDetail (same queryFn shape: raw Axios response), so on most
  // navigations this renders from cache without an extra fetch.
  const projectIdInPath = location.pathname.match(/^\/projects\/([a-f0-9]{24})(?:\/|$)/i)?.[1];
  const { data: pathProjectRes } = useQuery({
    queryKey: qk.project(projectIdInPath),
    queryFn: () => apiHelpers.getProject(projectIdInPath!),
    enabled: Boolean(projectIdInPath),
  });
  const pathProject: any = pathProjectRes?.data?.data;
  const dormantStatus =
    projectIdInPath && (pathProject?.status === 'SUSPENDED' || pathProject?.status === 'ARCHIVED')
      ? (pathProject.status as 'SUSPENDED' | 'ARCHIVED')
      : null;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // The header toggle is a two-state shortcut over a three-state preference:
  // flipping it commits an explicit light/dark choice rather than leaving the
  // user on 'system', which is what Settings → Appearance exposes in full.
  const toggleDarkMode = () => setTheme(isDark ? 'light' : 'dark');

  const ghostInfo = isGhostSession() ? getGhostInfo() : null;

  return (
    <div className={cn('bg-background min-h-screen', ghostInfo && 'pt-9')}>
      {/* Ghost session banner */}
      {ghostInfo && (
        <div className="fixed left-0 right-0 top-0 z-[9999] bg-purple-600 px-4 py-1.5 text-center text-sm font-medium text-white shadow-md">
          Ghost session — viewing as <strong>{ghostInfo.userName}</strong>
          <span className="ml-2 opacity-75">(by {ghostInfo.impersonatedBy})</span>
          <span className="ml-2 opacity-75">| Expires in 1 hour | Close tab to end</span>
        </div>
      )}

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'bg-card fixed inset-y-0 left-0 z-50 w-64 transform border-r transition-transform duration-200 ease-in-out lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Sidebar Header */}
        <div className="flex h-16 items-center justify-between border-b px-4">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="bg-primary flex h-8 w-8 items-center justify-center rounded-lg">
              <span className="text-primary-foreground font-bold">T</span>
            </div>
            <span className="text-lg font-semibold">Tyashin</span>
          </Link>
          <button
            className="hover:bg-muted rounded-md p-2 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-2 py-4">
          {/* Main */}
          <div className="space-y-1">
            {mainNavigation
              .filter((item) => {
                if (item.superOnly) return user?.role === 'SUPER_ADMIN';
                if (item.requires) return can(user, item.requires);
                return true;
              })
              .map((item) => {
                const isActive =
                  location.pathname === item.href || location.pathname.startsWith(`${item.href}/`);
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={cn(
                      'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    )}
                  >
                    <item.icon className="h-5 w-5" />
                    {item.name}
                  </Link>
                );
              })}
          </div>

          {/* Settings Section */}
          <div className="mt-6">
            <p className="text-muted-foreground mb-2 px-3 text-xs font-semibold uppercase tracking-wider">
              Settings
            </p>
            <div className="space-y-1">
              {settingsNavigation
                .filter((item) => !item.requires || can(user, item.requires))
                .map((item) => {
                  const isActive =
                    item.href === '/settings'
                      ? location.pathname === '/settings'
                      : location.pathname === item.href ||
                        location.pathname.startsWith(`${item.href}/`);
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={cn(
                        'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      )}
                    >
                      <item.icon className="h-5 w-5" />
                      {item.name}
                    </Link>
                  );
                })}
            </div>
          </div>

          {/* Admin Section — visible only to SUPER_ADMIN */}
          {user?.role === 'SUPER_ADMIN' && (
            <div className="mt-6">
              <p className="text-muted-foreground mb-2 px-3 text-xs font-semibold uppercase tracking-wider">
                <Shield className="mr-1 inline-block h-3 w-3" />
                Admin
              </p>
              <div className="space-y-1">
                {adminNavigation.map((item) => {
                  const isActive =
                    location.pathname === item.href ||
                    location.pathname.startsWith(`${item.href}/`);
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={cn(
                        'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      )}
                    >
                      <item.icon className="h-5 w-5" />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </nav>

        {/* Sidebar Footer - User Info */}
        <div className="border-t p-4">
          <div className="flex min-w-0 items-center gap-2">
            <div className="min-w-0 flex-1">
              <UserCell
                id={user?.id}
                firstName={user?.firstName}
                lastName={user?.lastName}
                size="sm"
              />
            </div>
            <RoleBadge
              role={
                user?.role === 'SUPER_ADMIN' ? 'SUPER_ADMIN' : user?.teamRole || user?.role || ''
              }
              className="shrink-0"
            />
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Top Header */}
        <header className="bg-card/95 sticky top-0 z-30 flex h-16 items-center justify-between border-b px-4 backdrop-blur lg:px-6">
          {/* Mobile Menu Button */}
          <button
            className="hover:bg-muted rounded-md p-2 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </button>

          {/* Breadcrumb / Page Title could go here */}
          <div className="flex-1" />

          {/* Right side actions */}
          <div className="flex items-center gap-2">
            {/* Theme Toggle */}
            <button
              onClick={toggleDarkMode}
              aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
              className="hover:bg-muted rounded-md p-2"
            >
              {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>

            {/* A notification bell used to sit here with no handler and a
                permanently lit unread dot. There is no notification feed to
                open, and a badge that is always red teaches people to ignore
                badges. Removed until there is something real behind it. */}

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="hover:bg-muted flex items-center gap-2 rounded-md p-2"
              >
                <div className="bg-muted flex h-8 w-8 items-center justify-center rounded-full">
                  <span className="text-xs font-medium">
                    {user?.firstName?.[0]}
                    {user?.lastName?.[0]}
                  </span>
                </div>
                <ChevronDown className="hidden h-4 w-4 sm:block" />
              </button>

              {userMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                  <div className="bg-card absolute right-0 z-50 mt-2 w-48 rounded-lg border py-1 shadow-lg">
                    <div className="border-b px-4 py-2">
                      <p className="text-sm font-medium">
                        {user?.firstName} {user?.lastName}
                      </p>
                      <p className="text-muted-foreground text-xs">{user?.email}</p>
                    </div>
                    <Link
                      to="/settings"
                      className="hover:bg-muted block px-4 py-2 text-sm"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <Settings className="mr-2 inline-block h-4 w-4" />
                      Settings
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="hover:bg-muted w-full px-4 py-2 text-left text-sm text-red-600"
                    >
                      <LogOut className="mr-2 inline-block h-4 w-4" />
                      Logout
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Impersonation banner (shown in ghost-login tabs) */}
        {sessionStorage.getItem('impersonating_as') && (
          <div className="flex items-center justify-between border-b border-amber-300 bg-amber-50 px-4 py-2 dark:border-amber-700 dark:bg-amber-900/20">
            <div className="flex items-center gap-2 text-sm font-medium text-amber-800 dark:text-amber-200">
              <Shield className="h-4 w-4" />
              Ghost login — viewing as{' '}
              <strong>
                {user?.firstName} {user?.lastName}
              </strong>{' '}
              ({user?.email})
            </div>
            <button
              onClick={() => {
                sessionStorage.removeItem('impersonating_as');
                window.close();
              }}
              className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700"
            >
              End Session
            </button>
          </div>
        )}

        {/* Dormant-project banner (visible to the whole team) */}
        {dormantStatus && (
          <div className="border-b border-amber-300 bg-amber-50 px-4 py-2 dark:border-amber-700 dark:bg-amber-900/20">
            <div className="flex items-center gap-2 text-sm font-medium text-amber-800 dark:text-amber-200">
              <Shield className="h-4 w-4 shrink-0" />
              {dormantStatus === 'ARCHIVED'
                ? 'This project is archived — the website is offline and its live infrastructure has been removed. Your data is retained. Contact Tyashin support to restore service.'
                : 'This project is suspended — the website, public APIs, and background jobs are offline. Your data is retained. Contact Tyashin support to restore service.'}
            </div>
          </div>
        )}

        {/* Quarterly rules notice: modal on sign-in while due, banner after "later" */}
        <UserRulesNotice />

        {/* Page Content */}
        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
